import { query, customElement } from 'lit/decorators.js';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import { WEB_COMPONENT_POLICY_PANEL_LIVE_SEARCH } from '../../../../../../../webcomponents.constants';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';

import {
  LiveSearchFormValue,
  LiveSearchSearchableKeywords,
} from '../../../../utils/uiSettings/liveSearch/live-search.types';
import { html } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import {
  getLiveSearchSearchableKeywords,
  LIVE_SEARCH_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/liveSearch/live-search.values';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_LIVE_SEARCH)
export class LiveSearchWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  LiveSearchFormValue,
  LiveSearchSearchableKeywords
> {
  /**
   * Path for LiveSearch module.
   */
  protected path = {
    moduleId: ModuleId.LIVE_SEARCH,
    sectionId: SectionId.NONE,
  };

  /**
   * Searchable keywords for Live search section.
   */
  protected searchableKeywords = getLiveSearchSearchableKeywords();

  @query('#live-search-toggle')
  private liveSearchToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#live-search-info-text')
  private liveSearchInfoText: GzTextWebComponent;

  static get styles() {
    return GzPolicyPanelAccordionTemplateWebComponent.styles;
  }

  protected renderTitle() {
    return html`<gz-form-field id="live-search-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[LiveSearchSearchableKeywords.LiveSearch].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[LiveSearchSearchableKeywords.LiveSearch].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html`
      <gz-text id="live-search-info-text" fontSize="12" fontWeight="400" class="infoText">
        ${translateFn('Enables Live Search capabilities on target endpoints.')}<br />
        ${translateFn(
          'With Live Search, you can retrieve information about events and system statistics directly from online endpoints using OSquery, an SQL-compatible query system.'
        )}
      </gz-text>
    `;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html``;
  }

  /**
   * Create form group.
   */
  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        liveSearch: {
          enabled: this.liveSearchToggle,
        },
      },
    });
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'liveSearch'], {});
    }

    return value;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.liveSearch
   */
  protected getValueFromManager(): LiveSearchFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.LIVE_SEARCH]: LIVE_SEARCH_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.LIVE_SEARCH]: PolicyPanelManager.getInstance().getPolicy('uiSettings.liveSearch'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Set form group value.
   */
  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        liveSearch: {
          enabled: this.valueT.uiSettings.liveSearch.enabled,
        },
      },
    };
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.liveSearchInfoText, !event.detail.checked);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_LIVE_SEARCH: LiveSearchWebComponent;
  }
}
